import RightArrow from "assets/images/v2/right-arrow.svg";
import tasksChecklistAnimation from "assets/images/v2/tasks-checklist.gif";
import TogglePills from "components/v2/toggle-pills/TogglePills";
import "./Tasks.scss";
import { useSelector, useDispatch } from "react-redux";
import {
  getSelectedTaskId,
  setSelectedTaskId,
  getTaskFilter,
  setTaskFilter,
  TaskFilterType,
  getCurrentUserName,
  getProspectId,
  getLoanNumber,
  getLoanId,
  getDealId,
  getEncompassLoanId,
} from "features/authSlice";
import {
  getSessionHeldUnderReviewIds,
  addSessionHeldUnderReviewId,
  removeSessionHeldUnderReviewId,
} from "pages/dashboard/store/dashboardSlice";
import { getLoanProgress } from "pages/dashboard/store/dashboardSlice";
import { trackFSDocumentsUploaded } from "utilities/full-story";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NeedItem, NeedStatus, Document, NeedType } from "../../dashboardService";
import FileUploadePanel from "components/v2/file-upload-panel/FileUploadPanel";
import { Spinner } from "components/v2/spinner/spinner";
import useDesktopChecker from "hooks/useDesktopChecker";
import { useNeedsRefetchUntil } from "hooks/useNeedsRefetchUntil";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import useTabletChecker from "hooks/useTabletChecker";
import { LoadingPageProps } from "pages/dashboard/v2/loadingPageProps";
import { TASK_CARD_TITLE, TASK_CARD_HEADING_TEXT } from "utilities/Constant";
import Pill from "components/v2/pill/pill";
import { isNonEmptyString } from "utilities/string";
import ESignPanel from "pages/dashboard/home/v2/eSign-panel/ESignPanel";
import Verification from "pages/dashboard/v2/verification-post-app/verification/Verification";
import { VerificationType } from "pages/online-verification/verificationService";
import { getTaskStatus, getEffectiveTaskStatus, getEffectiveTaskStatusForCount, isNeedCompleted } from "utilities/needsHelper";

const NEEDS_ESIGN_REFETCH_DELAY_MS = 3000;
const MAX_NEED_STATUS_REFETCH_ATTEMPTS = 5;
const EMPTY_HELD_UNDER_REVIEW_IDS: string[] = [];

const isNeedStillInTodoTab = (item: NeedItem | undefined): boolean =>
  !!item && getTaskStatus(item) === TaskFilterType.ToDo;

interface ActiveTask {
  needId: string;
  title: string;
  subTitle?: string;
  assignedTo?: string;
  note?: string;
  documents?: Document[];
  needType?: NeedType;
  needStatus?: NeedStatus;
  lat?: string;
  packageId?: string;
  isEnabled?: boolean;
}

interface TaskProps {
  title: string;
  subTitle: string;
  isSelected?: boolean;
  isComments?: boolean;
  handleClick?: () => void;
}

const Task = (props: TaskProps) => {
  const { title, subTitle, handleClick, isSelected, isComments } = props;
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  return (
    <button className={`task-item ${(isSelected && (isDesktop || isTablet)) ? "selected" : ""}`} onClick={handleClick}>
      <div className="task-item__text">
        <div className="task-item__title paragraph-medium-medium">{title}</div>
        <div className="task-item__pills-container">
          {isNonEmptyString(subTitle) && (
            <div className="task-item__subtitle">
              <Pill text={subTitle} variant="assigned" />
            </div>
          )}
          {isComments && (
            <div className="task-item__comments">
              <Pill text="New Comments" variant="comments" />
            </div>
          )}
        </div>
      </div>
      {!isDesktop && <img src={RightArrow} alt="Right arrow" />}
    </button>
  );
};

export interface TasksPageProps extends LoadingPageProps {
  isNeedsListLoading: boolean;
  isNeedsListFetching: boolean;
  isNeedsListError?: boolean;
  needsList: NeedItem[];
  refetchNeedsList?: () => Promise<{ data?: NeedItem[] } | undefined>;
}

const Tasks: React.FC<TasksPageProps> = ({
  onLoad,
  isNeedsListLoading,
  isNeedsListFetching,
  isNeedsListError = false,
  needsList = [],
  refetchNeedsList,
}: TasksPageProps) => {
  const isDesktop = useDesktopChecker();
  const isTablet = useTabletChecker();
  const [showUpload, setShowUpload] = useState(false);
  const [isMobileSubmitSuccess, setIsMobileSubmitSuccess] = useState(false);
  const [showVerificationDrawer, setShowVerificationDrawer] = useState(true);
  const [taskFilters, setTaskFilters] = useState<{ label: string; value: TaskFilterType; badge?: string; }[]>([
    { label: "To Do", value: TaskFilterType.ToDo },
    { label: "Under Review", value: TaskFilterType.UnderReview },
    { label: "Complete", value: TaskFilterType.Complete },
  ]);

  const [taskVOASimulation, setTaskVOASimulation] = useState<Partial<ActiveTask>>({});

  const selectedTaskIdFromRedux = useSelector(getSelectedTaskId);
  const taskFilter = useSelector(getTaskFilter);
  const dispatch = useDispatch();
  const username = useSelector(getCurrentUserName);
  const prospectId = useSelector(getProspectId);
  const dealId = useSelector(getDealId);
  const loanNumber = useSelector(getLoanNumber);
  const loanId = useSelector(getLoanId);
  const losId = useSelector(getEncompassLoanId);
  const loanProgress = useSelector(getLoanProgress);
  const hasInitializedRef = useRef(false);
  const sessionHeldUnderReviewIds = useSelector(getSessionHeldUnderReviewIds);
  const { data: featureFlag } = useGetFeatureFlagsQuery();
  const isEnhancedConditionsReviewSessionEnabled = featureFlag?.enhancedConditionsSessionHeldUnderReviewEnabled === true;
  const effectiveHeldUnderReviewIds = isEnhancedConditionsReviewSessionEnabled ? sessionHeldUnderReviewIds : EMPTY_HELD_UNDER_REVIEW_IDS;

  const scheduleStandardNeedsRefetchAfterDelay = useCallback(() => {
    setTimeout(() => {
      void refetchNeedsList?.();
    }, NEEDS_ESIGN_REFETCH_DELAY_MS);
  }, [refetchNeedsList]);

  const safeRefetchNeedsList = useCallback(async () => {
    if (!refetchNeedsList) {
      return undefined;
    }
    return refetchNeedsList();
  }, [refetchNeedsList]);

  const lastSubmittedNeedIdRef = useRef<string | null>(null);

  const shouldStopNeedsPolling = useCallback((list: NeedItem[], submittedNeedId: string) => {
    const item = list.find((n) => n.id === submittedNeedId);
    if (!item) {
      return false;
    }
    return !isNeedStillInTodoTab(item);
  }, []);

  const handlePollingSuccess = useCallback((id: string) => {
    if (!isEnhancedConditionsReviewSessionEnabled) {
      return;
    }

    const submittedNeed = needsList.find((n) => n.id === id);
    if (submittedNeed?.needType !== NeedType.EnhancedConditions) {
      return;
    }

    dispatch(addSessionHeldUnderReviewId(id));
  }, [dispatch, isEnhancedConditionsReviewSessionEnabled, needsList]);

  const {
    isPolling: isNeedsSubmitPolling,
    isExpectedStatusNotReceived: isNeedsSubmitExpectedStatusNotReceived,
    startPolling: startNeedsSubmitPolling,
    clearExpectedStatusError: clearNeedsSubmitExpectedStatusError,
  } = useNeedsRefetchUntil({
    refetch: safeRefetchNeedsList,
    isFetching: isNeedsListFetching,
    maxAttempts: MAX_NEED_STATUS_REFETCH_ATTEMPTS,
    shouldStop: shouldStopNeedsPolling,
    onSuccess: handlePollingSuccess,
  });

  const showTaskListLoader = isNeedsListLoading || isNeedsSubmitPolling;

  const getHeader = () => {
    const titleClassName = "heading-medium";
    const title = <h5 className={titleClassName}>{TASK_CARD_TITLE}</h5>;
    const heading = <span className="label-large-regular">{TASK_CARD_HEADING_TEXT}</span>;

    return (
      <div className="tasks__heading">
        {title}
        <section className="tasks__subhead">{heading}</section>
      </div>
    );
  };

  const handleTaskClick = (item: NeedItem) => {
    clearNeedsSubmitExpectedStatusError();
    dispatch(setSelectedTaskId(item.id));

    setActiveTaskId(item.id);

    if (!isDesktop) {
      setShowUpload(true);
    }

    if (item.needType === NeedType.Voa) {
      setShowVerificationDrawer(true);
    }
  };

  useEffect(() => {
    setShowUpload(isDesktop);
  }, [isDesktop]);

  const [activeTaskId, setActiveTaskId] = useState<string | null>(selectedTaskIdFromRedux ?? null);

  const filteredNeedsList = useMemo(() => {
    return needsList.filter(item => taskFilter === getEffectiveTaskStatus(item, effectiveHeldUnderReviewIds));
  }, [needsList, taskFilter, effectiveHeldUnderReviewIds]);


  const selectedTask: ActiveTask | null = useMemo(() => {
    if (!activeTaskId) return null;

    const task = needsList.find(item => item.id === activeTaskId);
    if (!task) return null;

    return {
      needId: task.id,
      title: task.title,
      assignedTo: task.assignedTo,
      note: task.note,
      needType: task.needType,
      documents: task.documents,
      needStatus: task.needStatus,
      lat: task.lat,
      packageId: task.packageId,
      isEnabled: task.isEnabled,
      ...taskVOASimulation
    };
  }, [activeTaskId, needsList, taskVOASimulation]);

  const handleDocumentUploadSubmitSuccess = useCallback((submittedFileCount: number) => {
    if (!selectedTask?.needId) {
      return;
    }

    trackFSDocumentsUploaded({
      username,
      timestamp: new Date().toISOString(),
      prospectId: prospectId,
      dealId: dealId || null,
      losLoanNumber: loanNumber || null,
      losId: losId || null,
      numberOfDocumentsUploaded: submittedFileCount,
      loanMilestone: loanProgress?.status ?? "Unknown",
    });

    const row = needsList.find((n) => n.id === selectedTask.needId);
    // Additional Documentation (`isDefault`): skip status polling; `updateDocumentIds` invalidates `document` (refetches getNeeds) — optional extra refetch for immediate UI sync.
    if (row?.isDefault !== true) {
      lastSubmittedNeedIdRef.current = selectedTask.needId;
      startNeedsSubmitPolling(selectedTask.needId);
    }
    // Keep panel open on mobile until user clicks "Back to Tasks"
    if (!isDesktop && !isTablet) {
      setIsMobileSubmitSuccess(true);
    }
  }, [needsList, startNeedsSubmitPolling, selectedTask?.needId, isDesktop, isTablet, username, prospectId, dealId, loanNumber, loanId, loanProgress]);

  // Fallback: if API status never updated after all retries, move the task to Under Review
  // optimistically for this session and suppress the error.
  useEffect(() => {
    if (!isEnhancedConditionsReviewSessionEnabled) {
      return;
    }

    if (isNeedsSubmitExpectedStatusNotReceived && lastSubmittedNeedIdRef.current) {
      const submittedNeed = needsList.find((n) => n.id === lastSubmittedNeedIdRef.current);
      if (submittedNeed?.needType === NeedType.EnhancedConditions) {
        dispatch(addSessionHeldUnderReviewId(lastSubmittedNeedIdRef.current));
      }
      clearNeedsSubmitExpectedStatusError();
      lastSubmittedNeedIdRef.current = null;
    }
  }, [isNeedsSubmitExpectedStatusNotReceived, dispatch, clearNeedsSubmitExpectedStatusError, isEnhancedConditionsReviewSessionEnabled, needsList]);

  const handleSetShowUpload = useCallback((show: boolean) => {
    setShowUpload(show);
    // Reset mobile submit success state when user navigates away
    if (!show) {
      setIsMobileSubmitSuccess(false);
    }
  }, []);

  useEffect(() => {
    if (!selectedTaskIdFromRedux) return;

    setActiveTaskId(selectedTaskIdFromRedux);

    if (!isDesktop) {
      setShowUpload(true);
    }

    dispatch(setSelectedTaskId(undefined));

  }, [selectedTaskIdFromRedux, isDesktop, dispatch]);

  useEffect(() => {
    // On mobile, keep showing the submitted task panel until user clicks "Back to Tasks"
    if (isMobileSubmitSuccess && !isDesktop && !isTablet) {
      return;
    }

    if (!filteredNeedsList.length) {
      setActiveTaskId(null);
      return;
    }

    const exists = filteredNeedsList.some(
      task => task.id === activeTaskId
    );

    if (!exists) {
      setActiveTaskId(filteredNeedsList[0].id);
    }

  }, [filteredNeedsList, activeTaskId, isMobileSubmitSuccess, isDesktop, isTablet]);

  useEffect(() => {
    setTaskVOASimulation({});
  }, [activeTaskId]);

  useEffect(() => {
    const updatedFilters = taskFilters.map(filter => {
      const count = needsList.filter(
        item => getEffectiveTaskStatusForCount(item, effectiveHeldUnderReviewIds) === filter.value
      ).length;

      return {
        ...filter,
        badge:
          filter.value === TaskFilterType.ToDo && count > 0
            ? count.toString()
            : ""
      };
    });

    setTaskFilters(updatedFilters);

  }, [needsList, effectiveHeldUnderReviewIds]);

  useEffect(() => {
    if (!isEnhancedConditionsReviewSessionEnabled) {
      return;
    }

    sessionHeldUnderReviewIds?.forEach((id) => {
      const item = needsList.find((n) => n.id === id);
      if (item && isNeedCompleted(item)) {
        dispatch(removeSessionHeldUnderReviewId(id));
      }
    });
  }, [needsList, sessionHeldUnderReviewIds, dispatch, isEnhancedConditionsReviewSessionEnabled]);

  useEffect(() => {
    if (hasInitializedRef.current) {
      return;
    }

    hasInitializedRef.current = true;
    dispatch(setTaskFilter(TaskFilterType.ToDo));
    onLoad?.();
  }, [dispatch, onLoad]);

  const handleClosedVerificationDrawer = () => {
    if (!isDesktop) setShowUpload(false);
    setShowVerificationDrawer(false);
  }

  // To simulate VOA completion, to be removed later
  const handleVerificationSuccess = () => {
    if (selectedTask) {
      setTaskVOASimulation({ ...selectedTask, needStatus: NeedStatus.Completed });
    }
  };

  return (
    <main className="tasks-main">
      {(isDesktop || !showUpload || isTablet) && (
        <section className={`tasks ${showUpload ? "hidden-mobile" : ""}`}>
          {getHeader()}
          <section className="tasks__current-complete">
            <TogglePills options={taskFilters} value={taskFilter} onChange={(value) => dispatch(setTaskFilter(value))} />
          </section>
          <section className="tasks__task-items">
            {showTaskListLoader ? (
              <div className="tasks__spinner-container">
                <Spinner isApi />
              </div>
            ) : isNeedsListError ? (
              <div className="tasks__needs-load-error" role="status" aria-live="polite">
                We are having issues loading your tasks. Try again later.
              </div>
            ) : isNeedsSubmitExpectedStatusNotReceived ? (
              <div className="tasks__error-state paragraph-medium-medium" role="status" aria-live="polite">
                Unable to retrieve the latest status. Please refresh the page to load the latest information.
              </div>
            ) : (
              <>
                {filteredNeedsList.map((item, idx) => {
                  const isSelected = selectedTask?.needId === item.id;
                  return (
                    <Task
                      key={idx}
                      title={item.title}
                      subTitle={item.assignedTo ?? ""}
                      isSelected={isSelected}
                      isComments={item.isNewComment || !!item.note?.trim()}
                      handleClick={() => {
                        handleTaskClick(item);
                      }}
                    />
                  );
                })}
                {isDesktop && filteredNeedsList.length === 0 && (
                  <div className="tasks__no-selected-tasks paragraph-medium-medium">
                    {taskFilter === TaskFilterType.Complete ? "No completed tasks yet" : "No current tasks"}
                  </div>
                )}
              </>
            )}
          </section>
        </section>
      )}
      <section className="tasks__selected">
        {filteredNeedsList.length === 0 && (
          <section className="tasks__no-tasks">
            <img src={tasksChecklistAnimation} alt="todo list being checked off with a pencil" />
            <span className="paragraph-medium">
              {taskFilter === TaskFilterType.ToDo
                ? "When you have tasks you will be able to view more details here."
                : taskFilter === TaskFilterType.Complete
                  ? "Your completed tasks will show up here"
                  : ""}
            </span>
          </section>
        )}
        {(isDesktop || showUpload) && filteredNeedsList.length > 0 && (
          selectedTask?.needType === NeedType.ESign ? (
            <ESignPanel
              status={selectedTask.needStatus || ""}
              loanAccessToken={selectedTask.lat}
              packageId={selectedTask.packageId}
              setShowUpload={handleSetShowUpload}
              isEnabled={selectedTask.isEnabled}
              onESignDrawerClosed={scheduleStandardNeedsRefetchAfterDelay}
            />
          ) : (
            selectedTask?.needType !== NeedType.Voa && (
              <FileUploadePanel
                needId={selectedTask?.needId || ""}
                needType={selectedTask?.needType}
                title={selectedTask?.title || ""}
                assignedTo={selectedTask?.assignedTo}
                setShowUpload={handleSetShowUpload}
                note={selectedTask?.note}
                documents={selectedTask?.documents}
                onSubmitSuccess={handleDocumentUploadSubmitSuccess}
              />
            )
          ))}
        {selectedTask?.needType === NeedType.Voa && showVerificationDrawer && (
          <div className="verification-container">
            <Verification
              verificationType={VerificationType.VOA}
              closeDrawerForMobile={handleClosedVerificationDrawer}
              status={selectedTask?.needStatus || ""}
              handlesuccessfullVerification={handleVerificationSuccess}
            />
          </div>
        )}
      </section>
    </main>
  );
};

export default Tasks;